import React, { useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import {
  LayoutDashboard,
  FileText,
  Gavel,
  TrendingUp,
  GraduationCap,
  Sparkles,
  CreditCard,
  Settings,
  Users,
  ChevronDown,
  ChevronRight,
  LogOut,
  Shield,
  Home,
  FileSearch,
  AlertTriangle,
  CheckCircle,
  Search,
  Clock,
  Layers,
  Target,
  BookOpen,
  Calculator,
  Wallet,
  Building,
  ShoppingBag,
  User,
  Bell,
  Link as LinkIcon,
  Archive,
  FileWarning,
  Scale,
  Wand2,
  Map,
  Building2,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import FundEliteBrandMark from './branding/FundEliteBrandMark';

const NavItem = ({ icon: Icon, label, to, children, isActive, onClick }) => {
  const [isOpen, setIsOpen] = useState(isActive);
  const hasChildren = children && children.length > 0;

  if (hasChildren) {
    return (
      <div className="mb-1">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`sidebar-nav-item w-full justify-between ${isActive ? 'active' : ''}`}
          data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <div className="flex items-center gap-3">
            <Icon size={20} strokeWidth={1.5} />
            <span className="sidebar-nav-text">{label}</span>
          </div>
          {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        {isOpen && (
          <div className="mt-1 animate-fade-in">
            {children.map((child, index) => (
              <Link
                key={index}
                to={child.to}
                className={`sidebar-nav-item sidebar-sub-item ${child.isActive ? 'active' : ''}`}
                data-testid={`nav-sub-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span>{child.label}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={to}
      onClick={onClick}
      className={`sidebar-nav-item ${isActive ? 'active' : ''}`}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon size={20} strokeWidth={1.5} />
      <span className="sidebar-nav-text">{label}</span>
    </Link>
  );
};

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const currentPath = location.pathname;

  const isActive = (path) => currentPath === path || currentPath.startsWith(path + '/');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = useMemo(() => {
    const items = [
    { icon: Home, label: 'Home', to: '/dashboard' },
    {
      icon: Users,
      label: 'Client Cases',
      children: [
        { label: 'All Clients', to: '/cases', isActive: isActive('/cases') },
        { label: 'New Case', to: '/cases/new', isActive: isActive('/cases/new') },
        { label: 'Active Cases', to: '/cases/active', isActive: isActive('/cases/active') },
        { label: 'Pending Review', to: '/cases/pending', isActive: isActive('/cases/pending') },
        { label: 'Completed', to: '/cases/completed', isActive: isActive('/cases/completed') },
        { label: 'Archived', to: '/cases/archived', isActive: isActive('/cases/archived') }
      ]
    },
    {
      icon: FileText,
      label: 'Credit Report',
      children: [
        { label: 'Import Report', to: '/credit-report/import', isActive: isActive('/credit-report/import') },
        { label: 'Credit Report', to: '/credit-report', isActive: currentPath === '/credit-report' },
        { label: 'Report Analysis', to: '/credit-report/analysis', isActive: isActive('/credit-report/analysis') },
        { label: 'Negative Accounts', to: '/credit-report/negative', isActive: isActive('/credit-report/negative') },
        { label: 'Positive Accounts', to: '/credit-report/positive', isActive: isActive('/credit-report/positive') },
        { label: 'Inquiries', to: '/credit-report/inquiries', isActive: isActive('/credit-report/inquiries') },
        { label: 'Public Records', to: '/credit-report/public-records', isActive: isActive('/credit-report/public-records') },
        { label: 'Personal Info', to: '/credit-report/personal', isActive: isActive('/credit-report/personal') },
        { label: 'Addresses', to: '/credit-report/addresses', isActive: isActive('/credit-report/addresses') },
        { label: 'Employment', to: '/credit-report/employment', isActive: isActive('/credit-report/employment') },
        { label: 'Closed Accounts', to: '/credit-report/closed', isActive: isActive('/credit-report/closed') }
      ]
    },
    { icon: Wand2, label: t('nav.disputeWizard'), to: '/dispute-wizard' },
    {
      icon: Gavel,
      label: t('nav.disputeCenter'),
      children: [
        { label: t('nav.disputeOverview'), to: '/disputes', isActive: currentPath === '/disputes' },
        { label: t('nav.roundManager'), to: '/disputes/rounds', isActive: isActive('/disputes/rounds') },
        { label: t('nav.bureauLetters'), to: '/disputes/bureau-letters', isActive: isActive('/disputes/bureau-letters') },
        { label: t('nav.furnisherLetters'), to: '/disputes/furnisher-letters', isActive: isActive('/disputes/furnisher-letters') },
        { label: t('nav.aiMetro2Letters'), to: '/disputes/ai-letters', isActive: isActive('/disputes/ai-letters') },
        { label: t('nav.disputePlaybook'), to: '/disputes/playbook', isActive: isActive('/disputes/playbook') },
        { label: t('nav.disputeTracking'), to: '/disputes/tracking', isActive: isActive('/disputes/tracking') },
        { label: t('nav.draftHistory'), to: '/disputes/history', isActive: isActive('/disputes/history') }
      ]
    },
    { icon: Scale, label: 'Collection Letter Markup', to: '/collection-markup' },
    {
      icon: Calculator,
      label: t('nav.creditBuilder'),
      children: [
        { label: t('creditBuilder.utilizationManager'), to: '/credit-builder/tradelines', isActive: isActive('/credit-builder/tradelines') },
        { label: t('nav.cliPlanner'), to: '/credit-builder/cli-planner', isActive: isActive('/credit-builder/cli-planner') },
        { label: t('nav.paymentPlanner'), to: '/credit-builder/payment-planner', isActive: isActive('/credit-builder/payment-planner') },
        { label: t('creditBuilder.scoreSimulatorNav'), to: '/credit-builder/simulator', isActive: isActive('/credit-builder/simulator') },
        { label: t('creditBuilder.tradelineStrategyNav'), to: '/credit-builder/tradeline-strategy', isActive: isActive('/credit-builder/tradeline-strategy') },
      ]
    },
    {
      icon: Map,
      label: 'Blueprints',
      children: [
        { label: 'AU Blueprint', to: '/blueprints/authorized-user', isActive: isActive('/blueprints/authorized-user') },
        { label: 'Credit Union Blueprint', to: '/blueprints/credit-union', isActive: isActive('/blueprints/credit-union') },
        { label: 'Secured Loan Blueprint', to: '/blueprints/secured-loan', isActive: isActive('/blueprints/secured-loan') }
      ]
    },
    {
      icon: TrendingUp,
      label: 'Funding Readiness',
      children: [
        { label: 'Funding Score', to: '/funding', isActive: currentPath === '/funding' },
        { label: 'AI Funding Strategy', to: '/funding/strategy', isActive: isActive('/funding/strategy') },
        { label: 'Approval Blockers', to: '/funding/blockers', isActive: isActive('/funding/blockers') },
        { label: 'Bank Positioning', to: '/funding/bank', isActive: isActive('/funding/bank') },
        { label: 'Offer Marketplace', to: '/funding/offers', isActive: isActive('/funding/offers') }
      ]
    },
    {
      icon: GraduationCap,
      label: 'FundElite Academy',
      children: [
        { label: 'All Courses', to: '/academy', isActive: currentPath === '/academy' },
        { label: 'Credit Basics', to: '/academy/credit-basics', isActive: isActive('/academy/credit-basics') },
        { label: 'Dispute Strategy', to: '/academy/dispute-strategy', isActive: isActive('/academy/dispute-strategy') },
        { label: 'Funding Readiness', to: '/academy/funding-readiness', isActive: isActive('/academy/funding-readiness') },
        { label: 'Business Credit', to: '/academy/business-credit', isActive: isActive('/academy/business-credit') },
        { label: 'AU Blueprint', to: '/academy/authorized-user-blueprint', isActive: isActive('/academy/authorized-user-blueprint') },
        { label: 'Resources', to: '/academy/resources', isActive: isActive('/academy/resources') }
      ]
    },
    { icon: Sparkles, label: 'AI Assistant', to: '/ai-assistant' },
    { icon: CreditCard, label: 'Billing & Plans', to: '/billing' },
    {
      icon: Settings,
      label: 'Settings',
      children: [
        { label: 'Profile', to: '/settings/profile', isActive: isActive('/settings/profile') },
        { label: 'Security', to: '/settings/security', isActive: isActive('/settings/security') },
        { label: 'Connected Accounts', to: '/settings/connections', isActive: isActive('/settings/connections') },
        { label: 'Notifications', to: '/settings/notifications', isActive: isActive('/settings/notifications') }
      ]
    }
  ];

    if (user?.role === 'admin') {
      items.push({
        icon: Shield,
        label: 'Admin Control',
        children: [
          { label: 'Overview', to: '/admin', isActive: currentPath === '/admin' },
          { label: 'Blueprint CMS', to: '/admin/blueprints', isActive: isActive('/admin/blueprints') },
          { label: 'Parser Training', to: '/admin/parser-training', isActive: isActive('/admin/parser-training') },
          { label: 'AI Knowledge Base', to: '/admin/knowledge-base', isActive: isActive('/admin/knowledge-base') },
          { label: 'Prompt Management', to: '/admin/prompts', isActive: isActive('/admin/prompts') },
          { label: 'Letter Training', to: '/admin/letter-training', isActive: isActive('/admin/letter-training') }
        ]
      });
    }

    return items;
  }, [t, currentPath, user?.role]);

  return (
    <aside className="sidebar" data-testid="sidebar">
      {/* Logo */}
      <div className="p-5 mb-2">
        <Link to="/dashboard" className="flex items-center" data-testid="sidebar-logo">
          <FundEliteBrandMark
            variant="self-repair"
            mode="logo"
            logoClassName="h-9 w-auto max-w-[168px] object-contain object-left"
          />
        </Link>
      </div>

      {/* Navigation */}
      <nav className="px-2 pb-4 flex-1">
        {navItems.map((item, index) => (
          <NavItem
            key={index}
            icon={item.icon}
            label={item.label}
            to={item.to}
            children={item.children}
            isActive={item.to ? isActive(item.to) : item.children?.some(c => c.isActive)}
          />
        ))}
      </nav>

      {/* User Profile Card */}
      <div className="p-4 mt-auto">
        <div className="glass-card p-3">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border-2 border-white/20">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-white/20 text-white font-semibold">
                {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="sidebar-profile-details flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-white/60 truncate">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
            <span className="text-xs px-2 py-1 rounded-full bg-white/15 text-white capitalize">
              {user?.plan || 'Free'} Plan
            </span>
            <button
              onClick={handleLogout}
              className="text-white/70 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-white/10"
              data-testid="logout-btn"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
